import type { RemixHiddenOptionKind } from '@atlassian/malleable-ui-components/common/types';

import { useCanPerformContentOperation } from '@confluence/entity-operations/entry-points/entityOperationsHelpers.ts';
import { OperationTypes } from '@confluence/entity-operations/entry-points/operationsTypes.ts';
import { expValEquals } from '@confluence/feature-experiments';
import { usePageContentId, usePageSpaceKey } from '@confluence/page-context';
import { useSpaceDetail } from '@confluence/space-utils/entry-points/useSpaceDetail.ts';

const NO_HIDDEN_OPTIONS: readonly RemixHiddenOptionKind[] = [];
const ATTACHMENT_PERMISSION_HIDDEN_OPTIONS = [
	'infographic',
	'image',
] as const satisfies readonly RemixHiddenOptionKind[];

/**
 * Hides options that cannot be completed in the current space from every Remix picker (editor,
 * object sidebar, and renderer). Diagrams finalize as whiteboards, while infographics and images
 * finalize as attachments.
 */
export const useShouldHideDiagramRemixOption = (): readonly RemixHiddenOptionKind[] => {
	const [spaceKey] = usePageSpaceKey();
	const { data: spaceDetail } = useSpaceDetail(spaceKey, {
		includeSpaceTypeSettings: true,
	});

	const isWhiteboardsEnabled =
		spaceDetail?.typeSettings?.enabledContentTypes?.isWhiteboardsEnabled ?? false;
	const enabledContentTypes = spaceDetail?.typeSettings?.enabledContentTypes;
	const hiddenOptions: RemixHiddenOptionKind[] = [];

	// Only hide the diagram option when whiteboards are unavailable for the space.
	if (!isWhiteboardsEnabled) {
		hiddenOptions.push('diagram');
	}

	if (!enabledContentTypes?.isWhiteboardsEnabled) {
		hiddenOptions.push('whiteboard');
	}
	if (!enabledContentTypes?.isDatabasesEnabled) {
		hiddenOptions.push('database');
	}
	if (!enabledContentTypes?.isSlidesEnabled) {
		hiddenOptions.push('slides');
	}
	return hiddenOptions.length > 0 ? hiddenOptions : NO_HIDDEN_OPTIONS;
};

/**
 * Resolves attachment-backed Remix eligibility independently from space-type settings so it can be
 * prefetched without moving the existing space-details query onto general page load.
 */
export const useShouldHideAttachmentRemixOptions = ({
	enabled = true,
}: {
	enabled?: boolean;
} = {}): readonly RemixHiddenOptionKind[] => {
	const [contentId] = usePageContentId();
	const shouldCheckPermission =
		enabled &&
		Boolean(contentId) &&
		expValEquals('cc_maui_remix_attachment_permissions', 'isEnabled', true);
	const canCreateAttachment = useCanPerformContentOperation({
		contentId: shouldCheckPermission ? (contentId ?? '') : '',
		operation: OperationTypes.CREATE,
		contentType: 'attachment',
	});

	// Fail closed for the options only. Modal opening never waits for this permission to resolve.
	return shouldCheckPermission && canCreateAttachment !== true
		? ATTACHMENT_PERMISSION_HIDDEN_OPTIONS
		: NO_HIDDEN_OPTIONS;
};
